import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useCrmAccess } from "@/hooks/use-crm-access";
import { Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Building, 
  Calendar,
  Download,
  Edit,
  FileCheck,
  FileText,
  Filter,
  Handshake,
  Mail,
  Phone,
  Plus,
  Search,
  Target,
  Trash2,
  User,
  UserX,
  UserCheck,
  UserPlus,
  File,
  FileSpreadsheet,
  FileImage,
  FileVideo
} from "lucide-react";
import { usePartners, useDeletePartner } from "@/hooks/use-partners";
import { usePartnerResources, useDeletePartnerResource } from "@/hooks/use-partner-resources";
import { usePartnerActiveOpportunities } from "@/hooks/use-partner-active-opportunities";
import { PartnerModal } from "@/components/crm/PartnerModal";
import { PartnerEditModal } from "@/components/crm/PartnerEditModal";
import { PartnerDetailModal } from "@/components/crm/PartnerDetailModal";
import { PartnerInviteButton } from "@/components/crm/PartnerInviteButton";
import { ResourceUploadModal } from "@/components/resources/ResourceUploadModal";
import { useState } from "react";
import { toast } from "sonner";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import { format } from "date-fns";

const CrmPartners = () => {
  const { data: hasCrmAccess, isLoading } = useCrmAccess();
  const { data: partners = [], isLoading: partnersLoading } = usePartners();
  const { data: resources = [], isLoading: resourcesLoading } = usePartnerResources();
  const { data: activeOpportunities = 0, isLoading: activeOpportunitiesLoading } = usePartnerActiveOpportunities();
  const deleteResource = useDeletePartnerResource();
  const deletePartner = useDeletePartner();
  const [selectedPartner, setSelectedPartner] = useState<any>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showResources, setShowResources] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!hasCrmAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleRowClick = (partner: any) => {
    setSelectedPartner(partner);
    setDetailModalOpen(true);
  };

  const handleEditPartner = (partner: any) => {
    setSelectedPartner(partner);
    setEditModalOpen(true);
  };

  const handleDeletePartner = (partner: any) => {
    if (window.confirm(`Are you sure you want to delete ${partner.first_name} ${partner.last_name}?`)) {
      deletePartner.mutate(partner.id);
    }
  };

  const filteredPartners = partners.filter(partner =>
    `${partner.first_name} ${partner.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    partner.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    partner.partner_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Signed':
        return 'bg-green-100 text-green-800';
      case 'Not-Signed':
        return 'bg-red-100 text-red-800';
      case 'Informal':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPartnerTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'Affiliate':
        return 'bg-blue-100 text-blue-800';
      case 'Associate':
        return 'bg-purple-100 text-purple-800';
      case 'Reseller':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTierBadgeColor = (tier: string) => {
    switch (tier) {
      case 'Platinum':
        return 'bg-slate-100 text-slate-800';
      case 'Gold':
        return 'bg-yellow-100 text-yellow-800';
      case 'Silver':
        return 'bg-gray-100 text-gray-700';
      case 'Bronze':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const categories = ["all", "marketing", "training", "sales", "technical", "legal"];
  
  const filteredResources = selectedCategory === "all" 
    ? resources 
    : resources.filter(r => r.category === selectedCategory);

  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this resource?")) {
      try {
        await deleteResource.mutateAsync(id);
        toast.success("Resource deleted successfully");
      } catch (error) {
        toast.error("Failed to delete resource");
      }
    }
  };

  const handleDownload = (url: string, title: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = title;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFileIcon = (fileType: string | null) => {
    if (!fileType) return File;
    
    const type = fileType.toLowerCase();
    
    if (type.includes('pdf')) return FileText;
    if (type.includes('image') || type.includes('png') || type.includes('jpg') || type.includes('jpeg')) return FileImage;
    if (type.includes('video') || type.includes('mp4') || type.includes('mov')) return FileVideo;
    if (type.includes('excel') || type.includes('spreadsheet') || type.includes('csv')) return FileSpreadsheet;
    if (type.includes('word') || type.includes('doc')) return FileText;
    if (type.includes('powerpoint') || type.includes('ppt')) return FileText;
    
    return File;
  };

  const getFileTypeColor = (fileType: string | null) => {
    if (!fileType) return 'text-slate-500';
    
    const type = fileType.toLowerCase();
    
    if (type.includes('pdf')) return 'text-red-500';
    if (type.includes('image')) return 'text-green-500';
    if (type.includes('video')) return 'text-purple-500';
    if (type.includes('excel') || type.includes('csv')) return 'text-emerald-500';
    if (type.includes('word') || type.includes('doc')) return 'text-blue-500';
    if (type.includes('powerpoint') || type.includes('ppt')) return 'text-orange-500';
    
    return 'text-slate-500';
  };

  if (partnersLoading) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-background">
          <AppSidebar />
          <main className="flex-1">
            <div className="p-8">
              <div className="text-slate-600">Loading partners...</div>
            </div>
          </main>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader 
            {...PAGE_HEADERS.crmPartners} 
            actions={
              <div className="flex gap-3">
                <Button 
                  onClick={() => setShowResources(!showResources)}
                  variant="outline"
                  size="sm"
                  className="gap-2 text-xs"
                >
                  <FileText className="h-3 w-3" />
                  View or add resources
                </Button>
                <PartnerModal />
              </div>
            } 
          />
          <div className="p-8 space-y-8" style={{ paddingTop: "8.5rem" }}>

             {/* Resources Section */}
             {showResources && (
               <div className="space-y-6">
                 <div className="flex items-center justify-between">
                   <div>
                     <h2 className="text-2xl font-bold tracking-tight">Partner Resources</h2>
                     <p className="text-muted-foreground">
                       Manage documents and resources for partners
                     </p>
                   </div>
                    <Button 
                      onClick={() => setShowUploadModal(true)}
                      style={{ backgroundColor: '#DFEF97', color: 'black' }}
                      className="hover:opacity-90"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Upload Resource
                    </Button>
                 </div>

                 {/* Category Filter */}
                 <div className="flex items-center space-x-2">
                   <Filter className="h-4 w-4 text-muted-foreground" />
                   <div className="flex space-x-2">
                     {categories.map((category) => (
                        <Button
                          key={category}
                          variant={selectedCategory === category ? "default" : "outline"}
                          size="sm"
                          onClick={() => setSelectedCategory(category)}
                          className={selectedCategory === category ? "bg-[hsl(60,73%,76%)] text-black hover:bg-[hsl(60,73%,70%)]" : ""}
                        >
                         {category.charAt(0).toUpperCase() + category.slice(1)}
                       </Button>
                     ))}
                   </div>
                 </div>

                 {/* Resources Grid */}
                 <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                   {filteredResources.map((resource) => {
                     const FileIcon = getFileIcon(resource.file_type);
                     const iconColor = getFileTypeColor(resource.file_type);
                     
                     return (
                       <Card key={resource.id} className="group hover:shadow-md transition-all duration-200 border-slate-200 bg-white hover:border-slate-300">
                         <CardHeader className="pb-2 space-y-2">
                           <div className="flex items-start justify-between">
                             <div className={`p-1.5 rounded-md bg-slate-50 group-hover:bg-slate-100 transition-colors ${iconColor}`}>
                               <FileIcon className="h-4 w-4" />
                             </div>
                             <Badge 
                               variant="secondary" 
                               className="text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200"
                             >
                               {resource.category || "general"}
                             </Badge>
                           </div>
                           
                           <div>
                             <CardTitle className="text-sm font-semibold text-slate-800 line-clamp-2 leading-tight">
                               {resource.title}
                             </CardTitle>
                           </div>
                         </CardHeader>
                         
                         <CardContent className="pt-0 space-y-3">
                           {/* File Details */}
                           <div className="text-xs text-slate-500">
                             <div className="font-medium">
                               {resource.file_type?.split('/')[1]?.toUpperCase() || "FILE"} • {resource.file_size ? `${Math.round(resource.file_size / 1024)} KB` : "—"}
                             </div>
                             <div className="mt-1">
                               {resource.created_at ? format(new Date(resource.created_at), 'MMM d, yyyy') : "Unknown date"}
                             </div>
                           </div>
                           
                           {/* Actions */}
                           <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownload(resource.file_url, resource.title)}
                                className="flex-1 h-7 text-xs font-medium bg-[hsl(60,73%,76%)] text-black border-gray-300 hover:bg-[hsl(60,73%,70%)]"
                              >
                               <Download className="h-3 w-3 mr-1" />
                               Download
                             </Button>
                             <Button
                               size="sm"
                               variant="outline"
                               onClick={() => handleDelete(resource.id)}
                               disabled={deleteResource.isPending}
                               className="h-7 px-2 text-xs border-red-200 hover:border-red-300 hover:bg-red-50 text-red-600 hover:text-red-700"
                             >
                               <Trash2 className="h-3 w-3" />
                             </Button>
                           </div>

                           {/* Visibility Badge */}
                           {!resource.is_public_to_all_partners && (
                             <div className="pt-1">
                               <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                                 <div className="flex items-center gap-1">
                                   <div className="w-1 h-1 bg-orange-500 rounded-full"></div>
                                   Selected partners
                                 </div>
                               </Badge>
                             </div>
                           )}
                         </CardContent>
                       </Card>
                     );
                   })}
                 </div>

                 {filteredResources.length === 0 && (
                   <div className="text-center py-16 px-4">
                     <div className="max-w-sm mx-auto">
                       <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                         <FileText className="h-8 w-8 text-slate-400" />
                       </div>
                       <h3 className="text-lg font-semibold text-slate-800 mb-2">No resources found</h3>
                       <p className="text-slate-600 mb-6 text-sm leading-relaxed">
                         {selectedCategory === "all" 
                           ? "Start by uploading your first resource to share with partners."
                           : `No resources found in the ${selectedCategory} category. Try a different category or upload new resources.`}
                       </p>
                       <Button 
                         onClick={() => setShowUploadModal(true)}
                         className="bg-slate-800 hover:bg-slate-700 text-white"
                       >
                         <Plus className="h-4 w-4 mr-2" />
                         Upload First Resource
                       </Button>
                     </div>
                   </div>
                 )}
               </div>
             )}

             {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-slate-700">Total Partners</CardTitle>
                    <Handshake className="h-5 w-5 text-teal-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-800">{partners.length}</div>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-slate-700">Active Opportunities</CardTitle>
                    <Target className="h-5 w-5 text-green-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-800">
                    {activeOpportunitiesLoading ? "—" : activeOpportunities}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-slate-700">Total Revenue</CardTitle>
                    <FileCheck className="h-5 w-5 text-blue-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-slate-800">
                    £{partners.reduce((sum, p) => sum + (p.revenue_generated || 0), 0).toLocaleString()}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-slate-700">Deals Won</CardTitle>
                    <UserX className="h-5 w-5 text-orange-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-800">
                    {partners.reduce((sum, p) => sum + (p.deals_won || 0), 0)}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Search */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 h-4 w-4" />
                <Input
                  placeholder="Search partners..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-slate-300 focus:border-teal-500 focus:ring-teal-500 rounded-lg bg-white"
                />
              </div>
            </div>

            {/* Partners Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPartners.map((partner) => (
                <Card 
                  key={partner.id}
                  className="group hover:shadow-lg transition-all duration-300 border border-slate-200 hover:border-slate-300 bg-white overflow-hidden hover:-translate-y-1"
                >
                  {/* Header without teal gradient */}
                  <div className="h-1 bg-gradient-to-r from-slate-200 to-slate-300 shadow-sm"></div>
                  
                  <CardHeader className="pb-4 bg-white">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        {partner.profile_image ? (
                          <img
                            src={partner.profile_image}
                            alt={`${partner.first_name} ${partner.last_name}`}
                            className="w-14 h-14 rounded-full object-cover border shadow-sm ring-1"
                            style={{ borderColor: "black", boxShadow: "0 0 0 1px black" }}
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center border shadow-sm ring-1" style={{ borderColor: "black", boxShadow: "0 0 0 1px black" }}>
                            <span className="text-black font-bold text-lg">
                              {partner.first_name?.[0] || ''}{partner.last_name?.[0] || ''}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-lg text-slate-900 group-hover:text-slate-700 transition-colors duration-200 truncate">
                            {partner.first_name} {partner.last_name}
                          </h3>
                          <div className="flex gap-1 mt-1">
                            <Badge 
                              className="text-xs font-semibold px-3 py-1 rounded-full shadow-sm text-black"
                              style={{ backgroundColor: "white" }}
                            >
                              {partner.partner_type}
                            </Badge>
                            <Badge className={`text-xs ${getStatusColor(partner.status)}`}>
                              {partner.status}
                            </Badge>
                            {/* User Status Badge */}
                            {partner.user_id && partner.portal_access_enabled ? (
                              <Badge 
                                className="text-xs font-semibold cursor-default text-black"
                                style={{ backgroundColor: "#DFEF97", borderColor: "black" }}
                              >
                                <UserCheck className="h-3 w-3 mr-1" />
                                Active User
                              </Badge>
                            ) : partner.invitation_sent_at ? (
                              <Badge className="bg-orange-100 text-orange-800 text-xs">
                                <Mail className="h-3 w-3 mr-1" />
                                Invite Sent
                              </Badge>
                            ) : (
                              <Badge className="bg-slate-100 text-slate-600 text-xs">
                                <UserPlus className="h-3 w-3 mr-1" />
                                No Access
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditPartner(partner)}
                          className="hover:bg-slate-50 hover:text-slate-700 hover:scale-105 transition-all duration-200"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePartner(partner)}
                          className="hover:bg-red-50 hover:text-red-600"
                          disabled={deletePartner.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0 space-y-4">
                    {/* Contact Information */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-3 text-sm text-slate-700 hover:text-slate-900 transition-colors duration-150 group/item">
                          <div className="flex items-center justify-center h-8 w-8 bg-slate-50 rounded-lg group-hover/item:bg-slate-100 transition-colors duration-150">
                            <Mail className="h-4 w-4 text-black" />
                          </div>
                          <span className="truncate font-medium">{partner.email}</span>
                        </div>
                      {partner.phone && (
                          <div className="flex items-center gap-3 text-sm text-slate-700 hover:text-slate-900 transition-colors duration-150 group/item">
                            <div className="flex items-center justify-center h-8 w-8 bg-slate-50 rounded-lg group-hover/item:bg-slate-100 transition-colors duration-150">
                              <Phone className="h-4 w-4 text-black" />
                            </div>
                            <span className="truncate font-medium">{partner.phone}</span>
                          </div>
                      )}
                    </div>

                    {/* Statistics Grid */}
                    <div className="grid grid-cols-4 gap-3 pt-2 border-t border-slate-100">
                      <div className="text-xs">
                        <span className="text-slate-500">Deals Won:</span>
                        <div className="font-semibold text-slate-800">{partner.deals_won || 0}</div>
                      </div>
                      <div className="text-xs">
                        <span className="text-slate-500">Revenue:</span>
                        <div className="font-semibold text-slate-800">£{(partner.revenue_generated || 0).toLocaleString()}</div>
                      </div>
                      <div className="text-xs">
                        <span className="text-slate-500">Commission:</span>
                        <div className="font-semibold text-slate-800">{partner.commission_rate || 10}%</div>
                      </div>
                      <div className="text-xs">
                        <span className="text-slate-500">Conv. Rate:</span>
                        <div className="font-semibold text-slate-800">{Math.min(100, partner.conversion_rate || 0)}%</div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRowClick(partner)}
                        className="w-full border-slate-300 text-slate-700 hover:shadow-sm transition-all duration-200 font-semibold bg-white"
                        style={{ borderColor: "black" }}
                      >
                        <User className="h-4 w-4 mr-2" />
                        View Details
                      </Button>
                      <PartnerInviteButton partner={partner} size="sm" variant="outline" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {filteredPartners.length === 0 && (
              <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
                <CardContent className="p-8 text-center">
                  <Handshake className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">No Partners Found</h3>
                  <p className="text-slate-600 mb-4">
                    {searchTerm ? "No partners match your search criteria." : "Get started by adding your first partner."}
                  </p>
                  {!searchTerm && <PartnerModal />}
                </CardContent>
              </Card>
            )}
          </div>
        </main>
      </div>
      
      {selectedPartner && (
        <>
          <PartnerDetailModal 
            partner={selectedPartner}
            open={detailModalOpen}
            onOpenChange={(open) => {
              setDetailModalOpen(open);
              if (!open) setSelectedPartner(null);
            }}
            onEdit={() => {
              setDetailModalOpen(false);
              handleEditPartner(selectedPartner);
            }}
          />
          <PartnerEditModal 
            partner={selectedPartner}
            open={editModalOpen}
            onOpenChange={(open) => {
              setEditModalOpen(open);
              if (!open) setSelectedPartner(null);
            }}
          />
        </>
      )}
      
      <ResourceUploadModal
        open={showUploadModal}
        onOpenChange={setShowUploadModal}
      />
    </SidebarProvider>
  );
};

export default CrmPartners;
